import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useGLTF, Box } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useThrottledFrame } from '../hooks/useThrottledFrame';
import { useLiteStore } from '../stores/liteStore';
// useXR removed — was causing unnecessary re-renders per environment model

interface SimpleEnvironmentModelProps {
  url: string;
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  isSelected?: boolean;
  /** 'none' skips auto-collider tagging — the model becomes pure scenery. */
  collision?: 'auto' | 'none';
}

const SimpleEnvironmentModel: React.FC<SimpleEnvironmentModelProps> = ({ 
  url, 
  position, 
  scale, 
  rotation,
  isSelected = false, // Changed default to false to reduce rendering
  collision = 'auto'
}) => {
  const { scene, materials } = useGLTF(url);
  const gl = useThree((s) => s.gl);
  const groupRef = useRef<THREE.Group>(null);
  const [boundingBox, setBoundingBox] = useState<THREE.Box3 | null>(null);
  // Performance: Track if transforms need updating
  const lastTransform = useRef({ position: [0, 0, 0], scale: 1, rotation: [0, 0, 0] });
  const needsUpdate = useRef(true);
  
  // VR perf handled by VRBuildModeOptimizer (shadow/particle/cull), not here
  // Recloning models on VR entry was causing slow load — removed
  
  // Clone and prepare scene with proper disposal
  const [preparedScene, autoScale] = useMemo(() => {
    // Note: Cannot access preparedScene here as it's being initialized
    // Cleanup is handled in the useEffect cleanup function
    
    const cloned = scene.clone();
    
    // Calculate bounding box for auto-centering
    const box = new THREE.Box3().setFromObject(cloned);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Auto-scale models to fit nicely in the scene
    const maxDim = Math.max(size.x, size.y, size.z);
    let calculatedAutoScale = 1;
    
    // Scale models based on their size
    if (maxDim > 1000) {
      calculatedAutoScale = 50 / maxDim;
    } else if (maxDim > 100) {
      calculatedAutoScale = 30 / maxDim;
    } else if (maxDim > 20) {
      calculatedAutoScale = 20 / maxDim;
    } else if (maxDim < 1) {
      calculatedAutoScale = 10;
    }
    
    // Apply VR quality reduction
    // vrQuality scaling removed — no longer reclone on VR entry
    
    // Center and ground the model
    cloned.position.x = -center.x;
    cloned.position.y = -box.min.y;
    cloned.position.z = -center.z;
    
    // Make the environment solid.
    //
    // Collision here is one axis-aligned box per mesh, so WHICH meshes are
    // solid decides whether a venue is walkable or a brick. Two strategies:
    //
    // 1. Authored proxies. If the model ships meshes named `collider_*` (or
    //    the Unreal-style `UCX_*`), those alone define the physical world and
    //    are hidden from the render. This is the good path: a stair becomes 24
    //    tread boxes the avatar can climb, a terrace becomes one flat slab, a
    //    railing becomes a thin wall. Nothing else is made solid — the visual
    //    meshes are free to be merged, baked, or decorative.
    //
    // 2. No proxies: fall back to making the visual meshes solid, skipping
    //    anything tiny so the player is not snagged on cups and leaves. This
    //    keeps older uploads working, but it degrades badly on baked exports
    //    where a whole building is one merged mesh — hence strategy 1.
    const COLLIDER_RE = /^(collider|ucx|collision)[_.]/i;
    const proxies: any[] = [];
    cloned.traverse((child: any) => {
      if (child.isMesh && COLLIDER_RE.test(child.name || '')) proxies.push(child);
    });

    if (collision === 'none') {
      console.log('[env] collision disabled for this model (scenery only)');
    } else if (proxies.length) {
      proxies.forEach((m) => {
        m.userData.collidable = true;
        m.visible = false;        // physical, never drawn
        m.castShadow = false;
        m.receiveShadow = false;
      });
      console.log(`[env] ${proxies.length} authored colliders`);
    } else {
      const _mb = new THREE.Box3();
      const _ms = new THREE.Vector3();
      let solid = 0;
      cloned.traverse((child: any) => {
        if (!child.isMesh) return;
        _mb.setFromObject(child);
        _mb.getSize(_ms);
        if (Math.max(_ms.x, _ms.y, _ms.z) < 0.35) return;   // decor
        child.userData.collidable = true;
        solid++;
      });
      if (solid) console.log(`[env] ${solid} collidable meshes (no authored colliders)`);
    }

    // Roofs on baked venues: the bake lights the interior, so a roof's upper
    // side comes out near-black — a slab of darkness from any balcony or
    // terrace above it. Overlay its up-facing triangles with a plain lit
    // roof finish. The overlay faces up only, so the ceiling inside is untouched.
    const roofs: any[] = [];
    cloned.traverse((child: any) => {
      if (child.isMesh && /roof/i.test(child.name || '') && !COLLIDER_RE.test(child.name || '')
        && child.material?.isMeshBasicMaterial && child.material.map) roofs.push(child);
    });
    if (roofs.length) {
      cloned.updateWorldMatrix(true, true);
      const roofMat = new THREE.MeshLambertMaterial({ color: '#4a443e', polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _n = new THREE.Vector3();
      const _nm = new THREE.Matrix3();
      for (const m of roofs) {
        const g = m.geometry as THREE.BufferGeometry;
        const pos = g.attributes.position;
        const idx = g.index;
        const tris = idx ? idx.count / 3 : pos.count / 3;
        _nm.getNormalMatrix(m.matrixWorld);
        const out: number[] = [];
        for (let t = 0; t < tris; t++) {
          const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
          _a.fromBufferAttribute(pos, i0); _b.fromBufferAttribute(pos, i1); _c.fromBufferAttribute(pos, i2);
          _n.subVectors(_b, _a).cross(_c.clone().sub(_a)).applyMatrix3(_nm).normalize();
          if (_n.y < 0.5) continue;
          out.push(_a.x, _a.y, _a.z, _b.x, _b.y, _b.z, _c.x, _c.y, _c.z);
        }
        if (!out.length) continue;
        const top = new THREE.BufferGeometry();
        top.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
        top.computeVertexNormals();
        const overlay = new THREE.Mesh(top, roofMat);
        overlay.name = `${m.name}_top`;
        overlay.raycast = () => {};
        m.add(overlay);
      }
      console.log(`[env] lit roof finish on ${roofs.length} roof mesh(es)`);
    }

    // Anisotropic filtering: venue walls, billboards and floors are mostly seen
    // at an angle, where plain mipmapping smears big images into a blur. 8x on
    // desktop, 4x on the lite tier (Quest/phones) to spare bandwidth.
    const aniso = Math.min(useLiteStore.getState().active ? 4 : 8, gl.capabilities.getMaxAnisotropy());
    if (aniso > 1) {
      cloned.traverse((child: any) => {
        if (!child.isMesh) return;
        for (const m of Array.isArray(child.material) ? child.material : [child.material]) {
          for (const slot of ['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
            const t = m?.[slot] as THREE.Texture | undefined;
            if (t && t.anisotropy < aniso) { t.anisotropy = aniso; t.needsUpdate = true; }
          }
        }
      });
    }

    // Store bounding box for selection visualization
    setBoundingBox(box);
    
    return [cloned, calculatedAutoScale];
  }, [scene, collision, gl]);
  
  // Only update transforms when they actually change (throttled — not needed every frame)
  useThrottledFrame(() => {
    if (!groupRef.current) return;
    
    // Check if transforms actually changed
    const posChanged = lastTransform.current.position[0] !== position[0] || 
                      lastTransform.current.position[1] !== position[1] || 
                      lastTransform.current.position[2] !== position[2];
    const scaleChanged = lastTransform.current.scale !== scale;
    const rotChanged = lastTransform.current.rotation[0] !== rotation[0] || 
                       lastTransform.current.rotation[1] !== rotation[1] || 
                       lastTransform.current.rotation[2] !== rotation[2];
    
    if (posChanged || scaleChanged || rotChanged || needsUpdate.current) {
      groupRef.current.position.set(position[0], position[1], position[2]);
      groupRef.current.scale.set(scale * autoScale, scale * autoScale, scale * autoScale);
      groupRef.current.rotation.set(rotation[0], rotation[1], rotation[2]);
      
      // Update last transform values
      lastTransform.current = {
        position: [...position],
        scale,
        rotation: [...rotation]
      };
      needsUpdate.current = false;
    }
  });
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Access preparedScene through a ref to avoid temporal dead zone issues
      const sceneToCleanup = groupRef.current?.children[0];
      if (sceneToCleanup) {
        sceneToCleanup.traverse((child: any) => {
          if (child.geometry) {
            child.geometry.dispose();
          }
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat: any) => {
                if (mat.dispose) mat.dispose();
              });
            } else if (child.material.dispose) {
              child.material.dispose();
            }
          }
        });
      }
      
      // Dispose materials
      if (materials) {
        Object.values(materials).forEach((material: any) => {
          if (material && material.dispose) {
            material.dispose();
          }
        });
      }
    };
  }, [materials]);
  
  // Calculate bounding box dimensions for selection box
  const boxSize = boundingBox ? [
    (boundingBox.max.x - boundingBox.min.x) * scale * autoScale,
    (boundingBox.max.y - boundingBox.min.y) * scale * autoScale,
    (boundingBox.max.z - boundingBox.min.z) * scale * autoScale
  ] : [1, 1, 1];
  
  return (
    // Transform lives on the element, not in a frame callback.
    //
    // It used to be applied inside useThrottledFrame, which VR skips wholesale
    // (__vrOptimizedMode drops every non-essential throttled callback). In a
    // headset the group therefore kept its defaults — position 0, scale 1 —
    // so a venue authored around a 1000-unit city rendered ~33x oversized and
    // ungrounded, dropping the player at the base of the building while
    // desktop looked perfect. Declaring it here means the model is placed on
    // mount and on every prop change, frame callbacks or not.
    <group
      ref={groupRef}
      position={position}
      scale={[scale * autoScale, scale * autoScale, scale * autoScale]}
      rotation={rotation as unknown as [number, number, number]}
      userData={{ isEnvironmentModel: true }}
    >
      <primitive object={preparedScene} />
      {isSelected && (
        <>
          <Box args={boxSize as [number, number, number]} position={[0, boxSize[1] / 2, 0]}>
            <meshBasicMaterial 
              color="#00c2b3" 
              wireframe 
              transparent 
              opacity={0.3} 
            />
          </Box>
          {/* Debug: Visual position indicator */}
          <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshBasicMaterial color="red" />
          </mesh>
        </>
      )}
    </group>
  );
};

export default SimpleEnvironmentModel;